/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import { ComponentManagerFn } from '../../../../registry/Component.type';
import { baseComponentManager } from '../../../../registry/Components';
import { toolTipProps } from '../../../../utils/commonProps';
import { defaultValue } from './AreaToolbarSave';

const AreaToolbarSaveManager: ComponentManagerFn<'AreaToolbarSave'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'AreaToolbarSave'>(platform, 'AreaToolbarSave'),
    type: 'AreaToolbarSave',
    name: 'Save',
    defaultValue,
    props: {
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      ...toolTipProps,
    },
    category: 'IoT',
    icon: { iconName: 'save' },
    description: `**Overview**<br/>
The Area Toolbar Save component is a dedicated action button designed to be placed within the **Area Toolbar** of the **IoT Image Mapper**. It provides the trigger mechanism for users to commit their changes—such as newly drawn zones, resized polygons, or updated attributes—permanently to the data source.
Use this component alongside the **Area Toolbar Edit** button to complete the editing workflow, ensuring users can persist their spatial modifications.

**1. Functionality**<br/>
* **Action:** Clicking this button initiates the "Save" sequence for the Image Mapper canvas. It collects the current geometry (SVG paths or coordinates) of all defined areas and updates the connected Data Store records.
* **Visual Icon:** Renders as a standard "Save" (floppy disk) icon within the toolbar, providing a familiar cue for persisting work.

**2. Permissions & Logic**<br/>
You can programmatically control when the save action is available to the user to maintain data integrity.
* **Disable Condition:** A boolean expression.
* **Usage:** Use this to prevent saving under specific criteria. For example, you might disable the button if the current user is in "Read-Only" mode (\`$.user.role === 'Viewer'\`) or if required zone attributes are missing.
* **Tooltip:** Supports standard tooltip properties. This allows you to show helper text like "Commit Changes to Server" or explain why the button is currently disabled (e.g., "No changes detected").

**3. Best Use Cases**<br/>
* **Admin Dashboards:** Providing a "Save" button for Facility Managers to commit updates after redrawing office cubicles or rearranging department boundaries on a floor plan.
* **Workflow Control:** Enabling the save button only after specific validation rules—such as ensuring every drawn zone has a unique Room ID—are met.
* **Configuration Management:** Saving new "Safe Zones" or "Restricted Areas" on industrial equipment schematics.`,
  };
};

export default AreaToolbarSaveManager;
